// src/features/rating_conversions/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { FormActionsNew } from "@/features/FormCard/Actions";
import { InputField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

// Import client name from filter.tsx for consistency
import { client } from "./filter";

const table_name = "rating_conversions";

// Zod schema for the new-rating-conversion form
const FormSchema = z.object({
  original_rating: z.string().min(1, "Original Rating is required"),
  rating_type: z.string().min(1, "Rating Type is required"),
  conversion_rating: z.coerce.number().optional().nullable(),
  final_rating: z.string().min(1, "Final Rating is required"),
});

export function RatingConversionNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Rating Conversion
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Original Rating & Rating Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="original_rating"
                    label={t(`${table_name}.columns.original_rating`, {
                      ns: "tables",
                      defaultValue: "Original Rating",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="rating_type"
                    label={t(`${table_name}.columns.rating_type`, {
                      ns: "tables",
                      defaultValue: "Rating Type",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* Row 2: Conversion Rating & Final Rating */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="conversion_rating"
                    label={t(`${table_name}.columns.conversion_rating`, {
                      ns: "tables",
                      defaultValue: "Conversion Rating",
                    })}
                    type="number"
                    allowZero
                    control={form.control}
                  />
                  <InputField
                    name="final_rating"
                    label={t(`${table_name}.columns.final_rating`, {
                      ns: "tables",
                      defaultValue: "Final Rating",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before actually creating the new record */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          {/* If user cancels out of the form */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
